import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import * as z from "zod"

import { AuthLayout } from "@repo/auth/components/auth-layout"
import { SignInForm } from "@repo/auth/components/sign-in-form"
import {
  getCurrentUserQueryOptions,
  sessionQueryKey,
  userQueryKey,
} from "@repo/auth/queries"
import { getCurrentUserPermissionsQueryOptions } from "@repo/permissions/queries"
import { generateMetaTags } from "@repo/seo/generate-meta-tags"
import { isAbsoluteUrl } from "@repo/tanstack-start-utils/utils/http"
import { pick } from "@repo/utils/collections"
import {
  getPostLoginRedirect,
  parsePublicRegistrationConfig,
  resolvePostSignInNavigateTarget,
} from "~/lib/auth/post-login-redirect"
import { sharedConfig } from "~/lib/config/shared"
import { CATCH_ALL_SLUG } from "~/lib/nav/catch-all-slugs"

const searchSchema = z.object({
  next: z
    .string()
    .optional()
    .refine(
      (v) => v === undefined || v === "" || !isAbsoluteUrl(v),
      "Invalid next",
    )
    .catch(""),
})

export const Route = createFileRoute("/_auth-layout/auth/sign-in")({
  component: SignInScreen,
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => pick(search, ["next"]),
  beforeLoad: async ({ context }) => {
    if (context.user) {
      const userPermissions = await context.queryClient.ensureQueryData(
        getCurrentUserPermissionsQueryOptions(context.user.id),
      )

      throw redirect(
        getPostLoginRedirect(
          userPermissions,
          parsePublicRegistrationConfig(
            context.publicModuleConfigs.registration,
          ),
        ),
      )
    }

    await context.queryClient.removeQueries()
  },
  head: ({ match }) => ({
    meta: generateMetaTags({
      base: {
        title: `Sign In | ${sharedConfig.appName}`,
        description: `Sign in to your ${sharedConfig.appName} account`,
        url: sharedConfig.baseUrl + match.fullPath,
      },
      robots: ["noindex", "nofollow", "noarchive"],
    }),
  }),
})

function SignInScreen() {
  const { publicModuleConfigs } = Route.useRouteContext()
  const { next } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const defaultNext = `/o/${CATCH_ALL_SLUG}/dashboard`

  const handleSignedIn = async () => {
    await queryClient.invalidateQueries({ queryKey: sessionQueryKey })
    await queryClient.invalidateQueries({ queryKey: userQueryKey })

    const user = await queryClient.fetchQuery(getCurrentUserQueryOptions())
    if (!user) {
      return
    }

    const userPermissions = await queryClient.fetchQuery(
      getCurrentUserPermissionsQueryOptions(user.id),
    )

    const target = resolvePostSignInNavigateTarget(
      next || defaultNext,
      userPermissions,
      parsePublicRegistrationConfig(publicModuleConfigs.registration),
    )

    if ("href" in target) {
      await navigate({ href: target.href, replace: true })
      return
    }

    await navigate({ ...target, replace: true })
  }

  const handleTwoFactorRequired = async (methods: string[]) => {
    await navigate({
      to: "/auth/2fa/challenge",
      search: { methods: methods.join(","), next: next || defaultNext },
      replace: true,
    })
  }

  return (
    <AuthLayout title="Sign in">
      <SignInForm
        onSignedIn={handleSignedIn}
        onTwoFactorRequired={handleTwoFactorRequired}
        showSignUp={!!publicModuleConfigs.registration?.enabled}
      />
    </AuthLayout>
  )
}
